/**
 * Select compacto del Admin sobre Radix Select: 40 px de alto, misma caja que `Input`.
 * Para listas largas o buscables usa `Combobox`.
 */
import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';

import { cn } from '../../lib/cn';

import type { Ref } from 'react';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  id?: string;
  name?: string;
  disabled?: boolean;
  invalid?: boolean;
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-required'?: true;
  className?: string;
  ref?: Ref<HTMLButtonElement>;
}

export function Select({
  value,
  defaultValue,
  onValueChange,
  options,
  placeholder,
  id,
  name,
  disabled,
  invalid,
  className,
  ...aria
}: SelectProps) {
  return (
    <SelectPrimitive.Root
      {...(value !== undefined ? { value } : {})}
      {...(defaultValue !== undefined ? { defaultValue } : {})}
      {...(onValueChange ? { onValueChange } : {})}
      {...(name ? { name } : {})}
      disabled={disabled}
    >
      <SelectPrimitive.Trigger
        id={id}
        aria-invalid={invalid ? true : undefined}
        className={cn(
          'inline-flex h-fm-control-sm w-full items-center justify-between gap-fm-2 rounded-fm-md',
          'border border-fm-border bg-fm-surface px-fm-3 text-fm-sm text-fm-text',
          'transition-colors duration-fm-fast ease-fm-standard motion-reduce:transition-none',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fm-focus',
          'aria-[invalid=true]:border-fm-danger disabled:opacity-45',
          className,
        )}
        {...aria}
      >
        <SelectPrimitive.Value placeholder={placeholder} />
        <SelectPrimitive.Icon>
          <ChevronDown size={16} aria-hidden="true" className="text-fm-text-secondary" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={4}
          className="z-50 max-h-[320px] min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-fm-md border border-fm-border bg-fm-surface shadow-fm-2"
        >
          <SelectPrimitive.Viewport className="p-fm-1">
            {options.map((option) => (
              <SelectPrimitive.Item
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                className={cn(
                  'relative flex h-fm-touch cursor-pointer select-none items-center gap-fm-2 rounded-fm-sm px-fm-3 text-fm-sm text-fm-text',
                  'data-[highlighted]:bg-fm-bg-sunken data-[highlighted]:outline-none',
                  'data-[disabled]:opacity-45 data-[disabled]:cursor-not-allowed',
                )}
              >
                <SelectPrimitive.ItemIndicator>
                  <Check size={16} aria-hidden="true" className="text-fm-primary-text" />
                </SelectPrimitive.ItemIndicator>
                <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}
